import { Checkbox, Form, InputNumber } from 'antd';
import { useContext } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { RULES } from 'app/components/pages/Hotels/HotelDetail/Finance/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { BasePriceConfiguration } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

import { useValidationErrors } from '../../hook/useValidationStore';

export const GuestConfiguration = () => {
  const { isViewMode, isEditMode } = useContext(ViewEditModeContext);

  const { guestsError } = useValidationErrors();

  const form = Form.useFormInstance();
  const minValue = Form.useWatch('minPax', form);
  const maxValue = Form.useWatch('maxPax', form);

  const priceDifferentiationMode = Form.useWatch(
    ['openingConfiguration', 'priceConfiguration', 'priceDifferentiationMode'],
    form
  );

  const handleAllowedPaxChange = (
    selectedPaxTypes: (string | number | boolean)[]
  ) => {
    const stringPaxTypes = selectedPaxTypes.filter(
      (val): val is string => typeof val === 'string'
    );

    if (!stringPaxTypes || stringPaxTypes.length === 0) {
      return;
    }

    const hasAdultOnly =
      stringPaxTypes.length === 1 && stringPaxTypes[0] === PAX_TYPE.ADULT;
    const hasChildOnly =
      stringPaxTypes.length === 1 && stringPaxTypes[0] === PAX_TYPE.CHILD;

    if (
      (hasAdultOnly || hasChildOnly) &&
      (priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
        priceDifferentiationMode ===
          PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME)
    ) {
      const newMode =
        priceDifferentiationMode ===
        PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
          ? PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
          : PRICE_DIFFERENTIATION_MODE.NONE;

      // Drop the pax-specific price fields that the new pax selection forbids,
      // on both the CONSTANT (configuration) and DAILY (days) shapes — otherwise
      // antd keeps the unmounted values and they fail validation with no
      // editable input left to clear them.
      const clearPaxFields = (
        config: BasePriceConfiguration = {}
      ): BasePriceConfiguration => ({
        ...config,
        childPrice: hasChildOnly ? config.childPrice : undefined,
        childDaytimePrice: hasChildOnly ? config.childDaytimePrice : undefined,
        price: hasAdultOnly ? config.price : undefined,
        daytimePrice: hasAdultOnly ? config.daytimePrice : undefined,
      });

      const priceConfiguration =
        form.getFieldValue(['openingConfiguration', 'priceConfiguration']) ??
        {};

      form.setFieldsValue({
        openingConfiguration: {
          priceConfiguration:
            priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY
              ? {
                  priceDifferentiationMode: newMode,
                  days: Object.fromEntries(
                    Object.entries(priceConfiguration.days ?? {}).map(
                      ([day, config]) => [
                        day,
                        clearPaxFields(config as BasePriceConfiguration),
                      ]
                    )
                  ),
                }
              : {
                  priceDifferentiationMode: newMode,
                  configuration: clearPaxFields(
                    priceConfiguration.configuration
                  ),
                },
        },
      });
    }
  };

  return (
    <SCard
      title="Guests"
      subtitle="Define which guests can access the activity, and with what restrictions."
      className="experience-form guests-configuration"
    >
      <FormLineLayout isRequired label="Allowed guests" disabled={isViewMode}>
        <Form.Item name="allowedPaxTypes" rules={RULES}>
          <Checkbox.Group
            disabled={isViewMode}
            onChange={handleAllowedPaxChange}
          >
            <Checkbox value={PAX_TYPE.ADULT}>Adults</Checkbox>
            <Checkbox value={PAX_TYPE.CHILD}>Children</Checkbox>
          </Checkbox.Group>
        </Form.Item>
        {isEditMode && !guestsError.isValid && (
          <span className="form-error-message">
            {guestsError.errors.map((error, index) => (
              <div key={index}>- {error.message}</div>
            ))}
          </span>
        )}
      </FormLineLayout>
      <FormLineLayout
        isOptional
        label="Min. guests"
        description="Set a min. guest when the price of the activity is for minimum x people."
        disabled={isViewMode}
      >
        <Form.Item name="minPax">
          {isEditMode ? (
            <InputNumber type="number" min={0} />
          ) : (
            <span className="view-data">{minValue || 'ø empty'}</span>
          )}
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout
        isOptional
        label="Max. guests"
        description="Set a max. guest when the price of the activity is for maximum x people."
        disabled={isViewMode}
      >
        <Form.Item name="maxPax">
          {isEditMode ? (
            <InputNumber type="number" min={0} />
          ) : (
            <span className="view-data">{maxValue || 'ø empty'}</span>
          )}
        </Form.Item>
      </FormLineLayout>
    </SCard>
  );
};
